"use client";
import type { SurveyQuestion } from "@/types/survey";
import { RadioGroup, Radio } from "@/design-system";

interface RadioBooleanQuestionProps {
  question: SurveyQuestion;
  brandColor?: string;
  isSelected?: (
    question: SurveyQuestion,
    candidate: string | number,
  ) => boolean;
  onAnswerChange?: (question: SurveyQuestion, value: string) => void;
  isPreview?: boolean;
  selectedValue?: string | number;
  onPreviewAnswerChange?: (questionId: string, value: string | number) => void;
  uiStrings?: {
    yes: string;
    no: string;
  };
}

export default function RadioBooleanQuestion({
  question,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  brandColor = "var(--color-alias-info)",
  isSelected,
  onAnswerChange,
  isPreview = false,
  selectedValue,
  onPreviewAnswerChange,
  uiStrings,
}: RadioBooleanQuestionProps) {
  const yesLabel = uiStrings?.yes ?? "Yes";
  const noLabel = uiStrings?.no ?? "No";
  const handleAnswerChange = (value: string | number) => {
    if (isPreview && onPreviewAnswerChange) {
      onPreviewAnswerChange(question.id, value);
    } else if (onAnswerChange) {
      onAnswerChange(question, value.toString());
    }
  };

  const isItemSelected = (value: string) => {
    if (isPreview) {
      return selectedValue === value;
    }
    return isSelected ? isSelected(question, value) : false;
  };

  if (isPreview) {
    // Preview mode - simpler display without RadioGroup
    return (
      <div className="mb-4 w-full">
        <div className="text-sm font-medium mb-2 text-left" style={{ color: "var(--sl-text, var(--color-alias-neutral-darkest))" }}>
          {question.text}
        </div>
        <div className="flex gap-4 pointer-events-none">
          <Radio
            name={question.id}
            label={yesLabel}
            value="true"
            checked={isItemSelected("true")}
            onChange={() => {}}
          />
          <Radio
            name={question.id}
            label={noLabel}
            value="false"
            checked={isItemSelected("false")}
            onChange={() => {}}
          />
        </div>
      </div>
    );
  }

  // Interactive mode - with RadioGroup
  const currentValue = isItemSelected("true")
    ? "true"
    : isItemSelected("false")
      ? "false"
      : null;

  return (
    <div className="mb-4">
      <div className="text-sm font-medium mb-2 text-left" style={{ color: "var(--sl-text, var(--color-alias-neutral-darkest))" }}>
        {question.text}
      </div>
      <RadioGroup
        name={question.id}
        orientation="horizontal"
        items={[
          { value: "true", label: yesLabel },
          { value: "false", label: noLabel },
        ]}
        value={currentValue}
        onValueChange={(value: unknown) => {
          if (typeof value === "string") {
            handleAnswerChange(value);
          }
        }}
      />
    </div>
  );
}
